import { useRouter } from "next/router";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { topic, tracking } from "shared/data";
import { ServiceJournalism } from "shared/data/tracking/module";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import { Edition } from "shared/lib/edition";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { thingsToDoHomeV2Query$key } from "scmp-app/queries/__generated__/thingsToDoHomeV2Query.graphql";

import { Container, Item, ItemsContainer, SectionTitle, SectionTitleLink } from "./styles";

const defaultTitle = "THINGS TO DO: HONG KONG";

type Props = {
  className?: string;
  reference: thingsToDoHomeV2Query$key;
};

type Settings = {
  custom_text?: string;
};

export const HomeV2ThingsToDo: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment thingsToDoHomeV2Query on Query {
        thingToDoV2Queue: queue(filter: { name: "homepage_service_journalism_hk" }) {
          settings
          items(first: 3) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...homeThingsToDoV2ContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const { query: routeQuery } = useRouter();
  const edition = routeQuery.edition as Edition;

  const items = data?.thingToDoV2Queue?.items?.edges ?? [];
  const customText =
    (data?.thingToDoV2Queue?.settings as Settings | undefined)?.custom_text ?? defaultTitle;
  const isDefaultTitle = customText.toUpperCase() === defaultTitle;
  const listOfArticles = items.map(({ node }) => node.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        widget_name: "things_to_do",
      },
      subcategory: "widget",
    }),
    [currentPageType, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const renderTitle = () =>
    isDefaultTitle ? (
      <SectionTitleLink
        as={topic.whatToDoInHongKong.urlAlias}
        pathname={topic.whatToDoInHongKong.urlAlias}
        query={{ module: ServiceJournalism }}
      >
        {customText}
      </SectionTitleLink>
    ) : (
      <SectionTitle>{customText}</SectionTitle>
    );

  if (edition !== Edition.HongKong) return null;
  if (items.length === 0) return null;

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.ThingsToDo }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        {renderTitle()}
        <ItemsContainer>
          {items.map(({ node }) => (
            <Item
              key={node.entityId}
              onClick={(entityId, urlAlias) => {
                sendGA4Tracking({
                  action: "click",
                  category: "recirculation",
                  customized_parameters: {
                    article_id: entityId,
                    destination_url: getAbsoluteUrl(urlAlias),
                    page_type: currentPageType,
                    widget_name: "things_to_do",
                  },
                  subcategory: "widget",
                });
              }}
              reference={node}
            />
          ))}
        </ItemsContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2ThingsToDo.displayName = "HomeV2ThingsToDo";
